import api from './api'
import { getAdminLocationLiveWorkers, getAdminWorkerBreadcrumbs, normalizeWorkerBreadcrumbs } from './access'
import type { WorkerBreadcrumbQuery } from '../types/access'
import type { ShiftHistoryDetail, ShiftHistoryTrail } from '../types/shiftHistory'

export const adminService = {
  getUsers: () => api.post('/api/listUserProfilesForAdmin'),

  getUserById: (email: string) => api.post('/api/getUserProfileForAdmin', { email }),

  updateUser: (email: string, data: any) =>
    api.post('/api/adminSaveUserProfile', { ...data, email }),

  deleteUser: (email: string) =>
    api.post('/api/adminSaveUserProfile', { email, puncStatus: 'inactive' }),

  getLocations: () => api.get('/locations', { headers: { 'x-role': 'admin' } }),

  createLocation: (data: any) => api.post('/locations', data, { headers: { 'x-role': 'admin' } }),

  getLocationById: (locationId: string) =>
    api.post('/api/listLocationsForAdmin').then((response: any) => ({
      ...response,
      data: {
        ...response.data,
        location: response.data?.locations?.find((location: any) => location.id === locationId),
      },
    })),

  updateLocation: (id: string, data: any) => api.post('/api/updateLocation', { ...data, id }),

  updateLocationDetails: (id: string, data: any) => api.put(`/locations/${id}`, data, { headers: { 'x-role': 'admin' } }),

  getRosters: (locationId?: string) =>
    api.post('/api/listLocationBreadcrumbsForSupervisor', {
      locationKey: locationId,
    }),

  getCorporateAssignments: () => api.post('/api/listCorporateAssignmentsForAdmin'),

  previewShiftCsv: (csvText: string) => api.post('/api/previewWorkerShiftCsv', { csvText }),

  importShiftCsv: (csvContent: string, fieldMap: Record<string, string> = {}, metadataFields: Array<{ key: string; value?: string }> = [], captureUnmapped = false) =>
    api.post('/api/importWorkerShiftsFromCsv', { csvContent, fieldMap, metadataFields, captureUnmapped }),

  listShiftCalendar: (filters: { startDate: number; endDate: number; locationKey?: string; workerId?: string }) =>
    api.post('/api/listWorkerShiftsForCalendar', filters),

  saveShiftAdjustments: (updates: any[]) => api.post('/api/saveCalendarShiftAdjustments', { updates }),

  getSupervisorDashboard: (locationKey?: string) =>
    api.post('/api/getLocationSupervisorDashboardData', { locationKey }, { headers: { 'x-role': 'corporate' } }),

  listSupervisorMessages: (locationKey: string) => api.post('/api/listLocationMessagesForSupervisor', { locationKey }),

  sendLocationMessage: (payload: any) => api.post('/api/sendLocationMessage', payload),

  sendSupervisorDirectMessage: (payload: any) => api.post('/api/sendSupervisorDirectMessage', payload),

  updatePunchApproval: (payload: any) => api.post('/api/updatePunchApproval', payload, { headers: { 'x-role': 'corporate' } }),

  getFenceMapperBootstrap: (ownerOrgId?: string) => api.post('/api/getFenceMapperBootstrap', ownerOrgId ? { ownerOrgId } : {}, { headers: { 'x-role': 'admin' } }),

  getActivePunchPush: (cursor?: string) => api.post('/api/getActivePunchPush', { cursor }, { headers: { 'x-role': 'admin' } }),

  saveLocationFromMapper: (payload: any) => api.post('/api/saveLocationFromMapper', payload, { headers: { 'x-role': 'admin' } }),

  deleteLocation: (id: string) => api.post('/api/deleteLocation', { id }, { headers: { 'x-role': 'admin' } }),

  importLocationsFromMyMaps: (payload: any) => api.post('/api/importLocationsFromMyMaps', payload, { headers: { 'x-role': 'admin' } }),

  listBillingPlans: () => api.get('/api/billing/plans'),

  listBillingAddons: () => api.get('/api/billing/addons'),

  listBillingProducts: () => api.get('/api/billing/products'),

  getBillingCurrent: () => api.get('/api/billing/current'),

  getBillingEntitlements: () => api.get('/api/billing/entitlements'),

  getMatchingAutofill: () => api.get('/api/admin/matching/autofill'),

  updateMatchingAutofillSettings: (payload: { executionEnabled: boolean; reason: string }) =>
    api.patch('/api/admin/matching/autofill/settings', payload),

  getTenantWorkerWorkLogAccess: () => api.get('/api/admin/settings/worker-worklog-access'),

  updateTenantWorkerWorkLogAccess: (payload: { enabled: boolean; additionalRoleIds?: string[] }) =>
    api.patch('/api/admin/settings/worker-worklog-access', payload),

  provisionTenantCanonicalWorkerRole: () =>
    api.post('/api/admin/settings/canonical-roles/worker/provision', {}),

  reconcileTenantCanonicalWorkerRole: () =>
    api.post('/api/admin/settings/canonical-roles/worker/reconcile-users', {}),

  selectBillingPlan: (payload: any) => api.post('/api/billing/select-plan', payload),

  enableBillingAddon: (payload: any) => api.post('/api/billing/addons/enable', payload),

  enableBillingProduct: (payload: any) => api.post('/api/billing/products/enable', payload),

  createBillingCheckoutSession: (payload: any = {}) => api.post('/api/billing/create-checkout-session', payload),

  createBillingPortalSession: (payload: any = {}) => api.post('/api/billing/create-portal-session', payload),

  getBillingUsage: () => api.get('/api/billing/usage'),

  getBillingOverages: () => api.get('/api/billing/overages'),

  getBillingInvoices: () => api.get('/api/billing/invoices'),

  getBillingInvoicePreview: () => api.get('/api/billing/invoice-preview'),

  listPlatformBillingTenants: (params: any = {}) => api.get('/api/platform/billing/tenants', { params }),

  listPlatformBillingAddons: () => api.get('/api/platform/billing/addons'),

  listPlatformBillingProducts: () => api.get('/api/platform/billing/products'),

  getPlatformBillingPricing: () => api.get('/api/platform/billing/pricing'),

  listPlatformPricingBooks: () => api.get('/api/platform/billing/pricing/books'),

  listPlatformPricingVersions: (params: any = {}) => api.get('/api/platform/billing/pricing/versions', { params }),

  listPlatformPricingRules: (params: any = {}) => api.get('/api/platform/billing/pricing/rules', { params }),

  listPlatformPricingTiers: (params: any = {}) => api.get('/api/platform/billing/pricing/tiers', { params }),

  listPlatformPricingOverages: (params: any = {}) => api.get('/api/platform/billing/pricing/overages', { params }),

  listPlatformPricingRegions: (params: any = {}) => api.get('/api/platform/billing/pricing/regions', { params }),

  listPlatformPricingOverrides: (params: any = {}) => api.get('/api/platform/billing/pricing/overrides', { params }),

  upsertPlatformPricingBook: (payload: any) => api.post('/api/platform/billing/pricing/books', payload),

  upsertPlatformPricingRule: (payload: any) => api.post('/api/platform/billing/pricing/rules', payload),

  upsertPlatformPricingTier: (ruleKey: string, payload: any) => api.post(`/api/platform/billing/pricing/rules/${ruleKey}/tiers`, payload),

  upsertPlatformPricingOverage: (ruleKey: string, payload: any) => api.post(`/api/platform/billing/pricing/rules/${ruleKey}/overages`, payload),

  upsertPlatformPricingRegion: (ruleKey: string, payload: any) => api.post(`/api/platform/billing/pricing/rules/${ruleKey}/regions`, payload),

  upsertPlatformTenantPricingOverride: (tenantId: string, payload: any) => api.post(`/api/platform/billing/tenants/${tenantId}/pricing-overrides`, payload),

  updatePlatformBillingAddon: (addonKey: string, payload: any) => api.patch(`/api/platform/billing/addons/${addonKey}`, payload),

  getPlatformBillingTenant: (tenantId: string) => api.get(`/api/platform/billing/tenants/${tenantId}`),

  getPlatformBillingTenantFeatures: (tenantId: string) => api.get(`/api/platform/billing/tenants/${tenantId}/features`),

  getPlatformTenantDocumentIntelligencePolicy: (tenantId: string) => api.get(`/api/system/tenants/${tenantId}/document-intelligence-policy`),

  updatePlatformTenantDocumentIntelligencePolicy: (tenantId: string, payload: { expectedRevision: number; policy: Record<string, unknown> }) =>
    api.put(`/api/system/tenants/${tenantId}/document-intelligence-policy`, payload),

  getPlatformTenantWorkerWorkLogAccess: (tenantId: string) => api.get(`/api/platform/billing/tenants/${tenantId}/worker-worklog-access`),

  updatePlatformTenantWorkerWorkLogAccess: (tenantId: string, payload: { enabled: boolean; additionalRoleIds?: string[] }) =>
    api.patch(`/api/platform/billing/tenants/${tenantId}/worker-worklog-access`, payload),

  provisionPlatformTenantCanonicalWorkerRole: (tenantId: string) =>
    api.post(`/api/platform/billing/tenants/${tenantId}/canonical-roles/worker/provision`, {}),

  reconcilePlatformTenantCanonicalWorkerRole: (tenantId: string) =>
    api.post(`/api/platform/billing/tenants/${tenantId}/canonical-roles/worker/reconcile-users`, {}),

  getPlatformBillingTenantInvoices: (tenantId: string) => api.get(`/api/platform/billing/tenants/${tenantId}/invoices`),

  updatePlatformTenantFeature: (tenantId: string, featureKey: string, payload: any) =>
    api.patch(`/api/platform/billing/tenants/${tenantId}/features/${encodeURIComponent(featureKey)}`, payload),

  updatePlatformAccelerationProfile: (tenantId: string, payload: any) =>
    api.patch(`/api/platform/billing/tenants/${tenantId}/acceleration-profile`, payload),

  updatePlatformStripeConfiguration: (tenantId: string, payload: any) =>
    api.patch(`/api/platform/billing/tenants/${tenantId}/stripe-configuration`, payload),

  updatePlatformBillingSubscription: (tenantId: string, payload: any) => api.patch(`/api/platform/billing/tenants/${tenantId}/subscription`, payload),

  changePlatformBillingPlan: (tenantId: string, payload: any) => api.post(`/api/platform/billing/tenants/${tenantId}/plan`, payload),

  createPlatformBillingPortalSession: (tenantId: string, payload: any) => api.post(`/api/platform/billing/tenants/${tenantId}/portal-session`, payload),

  previewPlatformBillingInvoice: (tenantId: string) => api.post(`/api/platform/billing/tenants/${tenantId}/invoice-preview`, {}),

  markPlatformInvoicePaid: (tenantId: string, payload: any = {}) => api.post(`/api/platform/billing/tenants/${tenantId}/mark-invoice-paid`, payload),

  updatePlatformTenantAddon: (tenantId: string, payload: any) => api.post(`/api/platform/billing/tenants/${tenantId}/addons`, payload),

  updatePlatformTenantProduct: (tenantId: string, payload: any) => api.post(`/api/platform/billing/tenants/${tenantId}/products`, payload),

  createPlatformUsageSnapshot: (tenantId: string) => api.post(`/api/platform/billing/tenants/${tenantId}/usage-snapshot`, {}),

  getPlatformSalesDashboard: () => api.get('/api/platform/sales/dashboard'),

  listPlatformSalesLeads: (params: any = {}) => api.get('/api/platform/sales/leads', { params }),

  updatePlatformSalesLead: (leadId: string, payload: any) => api.patch(`/api/platform/sales/leads/${leadId}`, payload),

  listPlatformSalesClients: (params: any = {}) => api.get('/api/platform/sales/clients', { params }),

  updatePlatformSalesClientAttribution: (tenantId: string, payload: any) =>
    api.patch(`/api/platform/sales/clients/${tenantId}/attribution`, payload),

  listPlatformSalesCommissions: (params: any = {}) => api.get('/api/platform/sales/commissions', { params }),

  updatePlatformSalesCommissionStatus: (commissionId: string, payload: any) =>
    api.patch(`/api/platform/sales/commissions/${commissionId}/status`, payload),

  listPlatformSalesPayouts: (params: any = {}) => api.get('/api/platform/sales/payouts', { params }),

  previewPlatformSalesPayoutStatement: (payload: any) => api.post('/api/platform/sales/payouts/preview', payload),

  createPlatformSalesPayoutStatement: (payload: any) => api.post('/api/platform/sales/payouts/statements', payload),

  getPlatformSalesPayoutStatement: (statementId: string) => api.get(`/api/platform/sales/payouts/statements/${statementId}`),

  approvePlatformSalesPayoutStatement: (statementId: string) =>
    api.post(`/api/platform/sales/payouts/statements/${statementId}/approve`, {}),

  markPlatformSalesPayoutStatementPaid: (statementId: string) =>
    api.post(`/api/platform/sales/payouts/statements/${statementId}/mark-paid`, {}),

  voidPlatformSalesPayoutStatement: (statementId: string) =>
    api.post(`/api/platform/sales/payouts/statements/${statementId}/void`, {}),

  getPlatformSalesPayoutExportUrl: (statementId: string, format: 'csv' | 'json') =>
    `/api/platform/sales/payouts/statements/${statementId}/export.${format}`,

  listPlatformSalesReps: (params: any = {}) => api.get('/api/platform/sales/reps', { params }),

  createPlatformSalesRep: (payload: any) => api.post('/api/platform/sales/reps', payload),

  updatePlatformSalesRep: (repId: string, payload: any) => api.patch(`/api/platform/sales/reps/${repId}`, payload),

  listPlatformReferralPartners: (params: any = {}) => api.get('/api/platform/sales/partners', { params }),

  createPlatformReferralPartner: (payload: any) => api.post('/api/platform/sales/partners', payload),

  listPlatformReferralLinks: (params: any = {}) => api.get('/api/platform/sales/referral-links', { params }),

  createPlatformReferralLink: (payload: any) => api.post('/api/platform/sales/referral-links', payload),

  updatePlatformReferralLink: (linkId: string, payload: any) => api.patch(`/api/platform/sales/referral-links/${linkId}`, payload),

  deactivatePlatformReferralLink: (linkId: string) => api.post(`/api/platform/sales/referral-links/${linkId}/deactivate`, {}),

  listPlatformReferralLinkClicks: (linkId: string, params: any = {}) => api.get(`/api/platform/sales/referral-links/${linkId}/clicks`, { params }),

  listPlatformReferralLinkConversions: (linkId: string, params: any = {}) => api.get(`/api/platform/sales/referral-links/${linkId}/conversions`, { params }),

  createPlatformSalesRepInvite: (salesRepId: string, payload: any) => api.post(`/api/platform/sales/reps/${salesRepId}/invites`, payload),

  listPlatformSalesRepInvites: (salesRepId: string, params: any = {}) => api.get(`/api/platform/sales/reps/${salesRepId}/invites`, { params }),

  revokePlatformSalesRepInvite: (inviteId: string) => api.post(`/api/platform/sales/invites/${inviteId}/revoke`, {}),

  uploadLocationJobReport: (payload: any) => api.post('/api/uploadLocationJobReport', payload),

  generatePayrollExport: (payload: any) => api.post('/api/generatePayrollExport', payload),

  pushPayrollExport: (payload: any) => api.post('/api/pushPayrollExport', payload),

  createCorporateUser: (payload: any) => api.post('/api/createCorporateUser', payload),

  assignLocationsToCorporateUser: (corporateEmail: string, locations: string[]) =>
    api.post('/api/assignLocationsToCorporateUser', { corporateEmail, locations }),

  listSupervisorBreadcrumbs: (payload: any) => api.post('/api/listLocationBreadcrumbsForSupervisor', payload, { headers: { 'x-role': 'corporate' } }),

  listAdminBreadcrumbs: (payload: any) => api.post('/api/listLocationTrackingBreadcrumbsForAdmin', payload, { headers: { 'x-role': 'admin' } }),

  getLocationStatus: (locationId: string) => api.get(`/api/admin/locations/${locationId}/status`, { headers: { 'x-role': 'corporate' } }),

  listLiveWorkersForLocation: (locationId: string, params: Pick<WorkerBreadcrumbQuery, 'includeLatestOnly' | 'limit'> = {}, signal?: AbortSignal) =>
    getAdminLocationLiveWorkers(locationId, params, signal).then((rows) => ({ data: { success: true, data: rows, workers: rows } })),

  listLocationBreadcrumbs: (locationId: string, params: WorkerBreadcrumbQuery = {}, signal?: AbortSignal) =>
    api.get(`/api/admin/locations/${locationId}/breadcrumbs`, { params, signal, headers: { 'x-role': 'corporate' } })
      .then((response) => ({ ...response, data: { ...response.data, data: normalizeWorkerBreadcrumbs(response.data) } })),

  listWorkerBreadcrumbs: (workerProfileId: string, params: WorkerBreadcrumbQuery = {}) =>
    getAdminWorkerBreadcrumbs(workerProfileId, params.locationId, params).then((trail) => ({ data: { success: true, data: trail } })),

  listShiftHistory: (params: Record<string, unknown> = {}, surface: 'admin' | 'supervisor' = 'admin') =>
    api.get(`/api/${surface}/shift-history`, { params }),

  getShiftHistory: (shiftGroupId: string, params: { locationId?: string } = {}, surface: 'admin' | 'supervisor' = 'admin') =>
    api.get<{ success: boolean; data: ShiftHistoryDetail }>(`/api/${surface}/shift-history/${encodeURIComponent(shiftGroupId)}`, { params }),

  getShiftHistoryTrail: (shiftGroupId: string, params: { locationId?: string } = {}, surface: 'admin' | 'supervisor' = 'admin') =>
    api.get<{ success: boolean; data: ShiftHistoryTrail }>(`/api/${surface}/shift-history/${encodeURIComponent(shiftGroupId)}/trail`, { params }),

  punchOutWorker: (locationId: string, workerId: string, reason: string, idempotencyKey?: string) =>
    api.post(
      `/api/admin/locations/${locationId}/workers/${workerId}/punch-out`,
      { reason, idempotencyKey },
      { headers: { 'x-role': 'corporate', ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}) } },
    ),

  getLocationTimesheets: (locationId: string, params: { from: string; to: string; workerId?: string }) =>
    api.get(`/api/admin/locations/${locationId}/timesheets`, { params, headers: { 'x-role': 'corporate' } }),

  approveTimesheetShift: (locationId: string, shiftId: string, notes?: string) =>
    api.post(`/api/admin/locations/${locationId}/timesheets/${shiftId}/approve`, { notes }, { headers: { 'x-role': 'corporate' } }),

  getTimesheetExportUrl: (locationId: string, format: 'csv' | 'xlsx' | 'pdf', params: { from: string; to: string; workerId?: string }) => {
    const query = new URLSearchParams(Object.entries(params).filter(([, value]) => Boolean(value)) as [string, string][]).toString()
    return `/api/admin/locations/${locationId}/timesheets/export.${format}?${query}`
  },

  downloadTimesheetExport: (locationId: string, format: 'csv' | 'xlsx' | 'pdf', params: { from: string; to: string; workerId?: string }) =>
    api.get(`/api/admin/locations/${locationId}/timesheets/export.${format}`, {
      params,
      headers: { 'x-role': 'corporate' },
      responseType: 'blob',
    }),

  listAdminMessages: () => api.get('/api/admin/messages', { headers: { 'x-role': 'corporate' } }),

  sendAdminMessage: (payload: any) => api.post('/api/admin/messages', payload, { headers: { 'x-role': 'corporate' } }),

  listLocationAdminMessages: (locationId: string) => api.get(`/api/admin/locations/${locationId}/messages`, { headers: { 'x-role': 'corporate' } }),

  listWorkerAdminMessages: (workerId: string) => api.get(`/api/admin/workers/${workerId}/messages`, { headers: { 'x-role': 'corporate' } }),

  archiveOrgUnit: (ouId: string, reason?: string) => api.patch(`/api/org-units/${ouId}/archive`, reason ? { reason } : {}),

  restoreOrgUnit: (ouId: string) => api.patch(`/api/org-units/${ouId}/restore`, {}),

  listWorkerDocumentsForManager: (workerProfileId: string) => api.get(`/api/admin/workers/${workerProfileId}/documents`),

  verifyWorkerDocument: (workerProfileId: string, documentId: string, notes?: string) =>
    api.post(`/api/admin/workers/${workerProfileId}/documents/${documentId}/verify`, notes ? { notes } : {}),

  rejectWorkerDocument: (workerProfileId: string, documentId: string, reason: string, notes?: string) =>
    api.post(`/api/admin/workers/${workerProfileId}/documents/${documentId}/reject`, { reason, notes }),
}
